import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { Field } from '@/components/ui/Field';
import { ChevronDownIcon, CloseIcon } from '@/components/ui/icons';
import { useTranslation } from '@/i18n';
import styles from './TimeZonePicker.module.css';

/**
 * The IANA zones this browser knows.
 *
 * Read defensively rather than through the type: `supportedValuesOf` is newer than
 * this project's `lib` setting, and a browser without it gets the plain text box the
 * field used to be rather than a crash.
 */
const ZONE_IDS = ((): readonly string[] => {
  try {
    const supported = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
    return supported ? supported('timeZone') : [];
  } catch {
    return [];
  }
})();

interface Zone {
  id: string;
  /** `UTC+05:00` — the offset today, so a zone with summer time shows today's. */
  offset: string;
  /** What a query is matched against: the id with spaces for underscores, and the
      offset written both ways, so `new york`, `+5` and `utc+05:00` all find. */
  search: string;
}

/** `UTC+05:00`, or empty for an id `Intl` does not accept. */
const offsetOf = (id: string, at: Date): string => {
  try {
    const name =
      new Intl.DateTimeFormat('en-US', { timeZone: id, timeZoneName: 'longOffset' })
        .formatToParts(at)
        .find((part) => part.type === 'timeZoneName')?.value ?? '';
    // `GMT+05:00`, and plain `GMT` at zero.
    return name === 'GMT' ? 'UTC+00:00' : name.replace('GMT', 'UTC');
  } catch {
    return '';
  }
};

/** `UTC+05:00` → `UTC+5`, `UTC+05:30` → `UTC+5:30`: how people type an offset. */
const shortOffset = (offset: string): string => {
  const match = /^UTC([+-])(\d\d):(\d\d)$/.exec(offset);
  if (!match) return '';
  const [, sign = '+', hours = '0', minutes = '00'] = match;
  return `UTC${sign}${Number(hours)}${minutes === '00' ? '' : `:${minutes}`}`;
};

const zoneOf = (id: string, at: Date): Zone => {
  const offset = offsetOf(id, at);
  return {
    id,
    offset,
    search: `${id.replace(/_/g, ' ')} ${offset} ${shortOffset(offset)}`.toLowerCase(),
  };
};

/**
 * Every zone with its offset, worked out on first use rather than at import: four
 * hundred formatters is a noticeable pause, and most visits to this screen never
 * open the list.
 */
let knownZones: readonly Zone[] | null = null;
const allZones = (): readonly Zone[] => {
  if (knownZones === null) {
    const now = new Date();
    knownZones = ZONE_IDS.map((id) => zoneOf(id, now));
  }
  return knownZones;
};

/**
 * The zones to offer: every known one, and the airport's own at the top if this
 * browser does not list it — an alias such as `Asia/Calcutta`, or a zone newer than
 * the browser. It is still the stored value, and the list must not pretend it is not.
 */
const zonesWith = (value: string): readonly Zone[] => {
  const zones = allZones();
  return value === '' || zones.some((zone) => zone.id === value) ? zones : [zoneOf(value, new Date()), ...zones];
};

const matching = (zones: readonly Zone[], query: string | null): readonly Zone[] => {
  const words = (query ?? '').toLowerCase().replace(/_/g, ' ').replace(/gmt/g, 'utc').split(/\s+/).filter(Boolean);
  return words.length === 0 ? zones : zones.filter((zone) => words.every((word) => zone.search.includes(word)));
};

/** Pixels: the list's tallest, its gap from the input, and its distance from the window's edge. */
const LIST_MAX = 288;
const GAP = 4;
const EDGE = 16;

/** Scrolls `scroller` just enough to show its `index`th option. */
const reveal = (scroller: HTMLElement, index: number, center = false) => {
  const item = scroller.querySelectorAll<HTMLElement>('[role="option"]')[index];
  if (!item) return;

  const top = item.offsetTop;
  const bottom = top + item.offsetHeight;

  if (center) scroller.scrollTop = top - (scroller.clientHeight - item.offsetHeight) / 2;
  else if (top < scroller.scrollTop) scroller.scrollTop = top;
  else if (bottom > scroller.scrollTop + scroller.clientHeight) scroller.scrollTop = bottom - scroller.clientHeight;
};

interface TimeZonePickerProps {
  label: string;
  /** An IANA zone id, or empty for none. */
  value: string;
  onChange: (zone: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

/**
 * The airport's time zone: an input to search with, and the zones beneath it.
 *
 * It replaced a text box with a `<datalist>`, which every browser draws differently
 * — Chrome's is a long grey menu of ids, Firefox's appears only once you type — and
 * which let anything at all be typed and saved. Here a zone is CHOSEN: typing only
 * narrows the list, and leaving the field puts back the zone that was there. Empty
 * is a choice too, since the field is optional, and the clear button makes it.
 *
 * Each zone shows its offset, because "Asia/Qyzylorda" does not say "+5" and the
 * offset is usually what the person setting it knows.
 *
 * **The list is a popover**, in the browser's top layer. In the create form this
 * sits inside a modal `<dialog>`, which is in the top layer itself — a list added to
 * `<body>` would be drawn UNDER it, and one positioned inside the form would be
 * clipped by the card's scrolling body. A popover is drawn above both, and its
 * position is set from the input's in code, again on every scroll and resize.
 *
 * The keyboard follows the combobox pattern: arrows move, Enter picks, Escape
 * closes the list — and is kept from closing the dialog around it.
 */
export const TimeZonePicker = ({ label, value, onChange, disabled = false, readOnly = false }: TimeZonePickerProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  /** What is typed while the list is open; null until something is. */
  const [query, setQuery] = useState<string | null>(null);
  const [active, setActive] = useState(0);

  const boxRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const options = useMemo(() => (isOpen ? matching(zonesWith(value), query) : []), [isOpen, query, value]);

  const input = () => boxRef.current?.querySelector('input') ?? null;

  const open = () => {
    if (isOpen || disabled || readOnly) return;
    setIsOpen(true);
    setQuery(null);
    setActive(Math.max(0, zonesWith(value).findIndex((zone) => zone.id === value)));
    // Selected, so the first key typed replaces the zone rather than adding to it.
    input()?.select();
  };

  const close = () => {
    setIsOpen(false);
    setQuery(null);
  };

  const pick = (zone: Zone | undefined) => {
    if (!zone) return;
    onChange(zone.id);
    close();
  };

  // Placed against the input, in the top layer, and kept there while anything
  // scrolls: the panel's body, the dialog's, or the page.
  useLayoutEffect(() => {
    const popover = popoverRef.current;
    const field = boxRef.current?.querySelector('input');
    if (!isOpen || !popover || !field) return undefined;

    const place = () => {
      const rect = field.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - GAP - EDGE;
      const above = rect.top - GAP - EDGE;
      // Under the input, unless there is not room for it there and there is more above.
      const upward = below < LIST_MAX && above > below;

      popover.style.minWidth = `${rect.width}px`;
      popover.style.maxHeight = `${Math.min(LIST_MAX, upward ? above : below)}px`;
      popover.style.top = upward ? 'auto' : `${rect.bottom + GAP}px`;
      popover.style.bottom = upward ? `${window.innerHeight - rect.top + GAP}px` : 'auto';
      // Against the input's left edge, unless that would run off the window's right.
      popover.style.left = `${Math.max(EDGE, Math.min(rect.left, window.innerWidth - popover.offsetWidth - EDGE))}px`;
    };

    try {
      popover.showPopover();
    } catch {
      // Already showing — StrictMode runs this twice in development, and some
      // browsers throw on a second show — or no Popover API, in which case the list
      // is still placed, just not above a dialog.
    }
    place();
    // The current zone in the middle of the list, as it opens with nothing typed.
    reveal(popover, Math.max(0, zonesWith(value).findIndex((zone) => zone.id === value)), true);

    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [isOpen, value]);

  useEffect(() => {
    if (popoverRef.current) reveal(popoverRef.current, active);
  }, [active, options]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (isOpen) setActive((index) => Math.min(index + 1, options.length - 1));
        else open();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) setActive((index) => Math.max(index - 1, 0));
        break;
      case 'Enter':
        // Picks rather than submits while the list is open: Enter on a highlighted
        // zone means that zone, not "save the airport".
        if (isOpen) {
          event.preventDefault();
          pick(options[active]);
        }
        break;
      case 'Escape':
        // Prevented, so the dialog this may sit in does not take the same key as
        // a request to close — this Escape is about the list, and the next is the
        // dialog's.
        if (isOpen) {
          event.preventDefault();
          close();
        }
        break;
    }
  };

  // Keeps the focus in the input: a click in the list would otherwise blur it, and
  // the blur closes the list before the click lands.
  const keepFocus = (event: MouseEvent) => event.preventDefault();

  if (ZONE_IDS.length === 0 || readOnly) {
    return (
      <Field
        label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        readOnly={readOnly}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }

  const activeOption = isOpen && options[active] ? `${listId}-${active}` : undefined;

  return (
    <div className={styles.box} ref={boxRef}>
      <Field
        label={label}
        value={isOpen && query !== null ? query : value}
        placeholder={t('airports.panel.timeZoneSearch')}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
          setIsOpen(true);
        }}
        onClick={open}
        onKeyDown={handleKeyDown}
        onBlur={close}
        disabled={disabled}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeOption}
        autoComplete="off"
        spellCheck={false}
        trailing={
          !disabled && (
            <>
              {value !== '' && (
                <button
                  type="button"
                  className={styles.icon}
                  onMouseDown={keepFocus}
                  onClick={() => {
                    onChange('');
                    close();
                  }}
                  aria-label={t('airports.panel.timeZoneClear')}
                  title={t('airports.panel.timeZoneClear')}
                >
                  <CloseIcon />
                </button>
              )}
              {/* Out of the tab order and unnamed: the input itself opens the list
                  from the keyboard, and this is only a larger place to click. */}
              <button
                type="button"
                className={styles.icon}
                onMouseDown={(event) => {
                  keepFocus(event);
                  input()?.focus();
                  if (isOpen) close();
                  else open();
                }}
                tabIndex={-1}
                aria-hidden="true"
              >
                <ChevronDownIcon />
              </button>
            </>
          )
        }
      />

      {isOpen && (
        <div ref={popoverRef} className={styles.popover} popover="manual" onMouseDown={keepFocus}>
          {options.length === 0 ? (
            <p className={styles.empty}>{t('airports.panel.timeZoneNone')}</p>
          ) : (
            <ul id={listId} className={styles.list} role="listbox" aria-label={label}>
              {options.map((zone, index) => (
                <li
                  key={zone.id}
                  id={`${listId}-${index}`}
                  className={styles.option}
                  role="option"
                  aria-selected={zone.id === value}
                  data-active={index === active}
                  onMouseMove={() => setActive(index)}
                  onClick={() => pick(zone)}
                >
                  {zone.id}
                  <span className={styles.offset}>{zone.offset}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
